/**
 * R138 · Feb 2026 — Accounts (Companies) list
 *
 * Light home for organizations pulled from external CRMs (Aquarius,
 * Apollo). Search + filter by industry / exporter-flag, click a row to
 * drill into that account's linked contacts (leads whose
 * external_company_id matches).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Search, Building2, Globe, MapPin, Ship, PartyPopper, X, Users } from "lucide-react";

const StatTile = ({ label, value, testid }) => (
  <div className="border border-border p-3 bg-background" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-2xl font-semibold">{value?.toLocaleString?.() ?? value ?? "—"}</div>
  </div>
);

export default function Accounts() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [exporter, setExporter] = useState("");
  const [busy, setBusy] = useState(false);
  const [openAccount, setOpenAccount] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsBusy, setContactsBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (industry) params.set("industry", industry);
      if (exporter !== "") params.set("exporter", exporter);
      params.set("limit", "500");
      const [{ data: rr }, { data: ss }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/accounts?${params}`),
        api.get(`/workspaces/${activeWorkspaceId}/accounts/stats`),
      ]);
      setRows(rr || []);
      setStats(ss || {});
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load accounts")); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (activeWorkspaceId) load(); }, [activeWorkspaceId]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q, industry, exporter]); // eslint-disable-line

  const industries = useMemo(() => {
    const s = new Set(rows.map((r) => r.industry).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const openDrawer = async (acc) => {
    setOpenAccount(acc);
    setContactsBusy(true);
    setContacts([]);
    try {
      const { data } = await api.get(`/accounts/${acc.id}/contacts`);
      setContacts(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load contacts")); }
    finally { setContactsBusy(false); }
  };

  return (
    <div className="space-y-4 px-4 sm:px-6 md:px-10" data-testid="accounts-page">
      <PageHeader
        eyebrow="CRM"
        title="Accounts"
        description="Companies pulled from your CRM or import files. Drill in to see every contact working at each organization."
        testid="accounts-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none"
                    onClick={() => nav("/app/data-import?entity=accounts")}
                    data-testid="accounts-import-btn">
              <Upload className="w-4 h-4 mr-2" /> Bulk import
            </Button>
          </div>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatTile label="Total companies" value={stats.total} testid="accounts-stat-total" />
        <StatTile label="Exporters" value={stats.exporters} testid="accounts-stat-exporters" />
        <StatTile label="Past exhibition" value={stats.with_past_exhib} testid="accounts-stat-past-exhib" />
        <StatTile label="With website" value={stats.with_website} testid="accounts-stat-with-website" />
      </div>

      <div className="flex flex-wrap items-center gap-2 border border-border p-2 bg-muted/20">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name, website, email, industry…"
                 className="pl-8 h-8 text-xs"
                 data-testid="accounts-search-input" />
        </div>
        <select value={industry} onChange={(e) => setIndustry(e.target.value)}
                className="h-8 text-xs border border-border bg-background px-2"
                data-testid="accounts-industry-filter">
          <option value="">All industries</option>
          {industries.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select value={exporter} onChange={(e) => setExporter(e.target.value)}
                className="h-8 text-xs border border-border bg-background px-2"
                data-testid="accounts-exporter-filter">
          <option value="">Any</option>
          <option value="true">Exporters only</option>
          <option value="false">Non-exporters</option>
        </select>
        <div className="text-[11px] text-muted-foreground ml-auto" data-testid="accounts-count">
          {busy && rows.length === 0 ? "Loading…" : `${rows.length} companies`}
        </div>
      </div>

      <div className="border border-border overflow-hidden">
        <table className="w-full text-xs" data-testid="accounts-table">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Company</th>
              <th className="text-left px-3 py-2 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Industry</th>
              <th className="text-left px-3 py-2 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[80px]">Country</th>
              <th className="text-center px-3 py-2 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[70px]">Exporter</th>
              <th className="text-left px-3 py-2 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[110px]">Past exhib.</th>
              <th className="text-right px-3 py-2 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[80px]">Contacts</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}
                  className="border-t border-border cursor-pointer hover:bg-muted/30"
                  onClick={() => openDrawer(r)}
                  data-testid={`account-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate max-w-[220px]" title={r.name}>{r.name}</span>
                    {r.website && (
                      <a href={r.website.startsWith("http") ? r.website : `https://${r.website}`}
                         target="_blank" rel="noopener noreferrer"
                         onClick={(e) => e.stopPropagation()}
                         className="text-muted-foreground hover:text-primary"
                         title={r.website}>
                        <Globe className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-[180px]">
                  {r.industry || "—"}
                  {r.sub_industry && <span className="text-[10px] ml-1 opacity-70">· {r.sub_industry}</span>}
                </td>
                <td className="px-3 py-2 text-muted-foreground text-[11px]">
                  {r.country ? <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.country}</span> : "—"}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.is_exporter === true ? <Ship className="w-3.5 h-3.5 inline text-sky-700" data-testid={`account-exporter-${r.id}`} /> : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-[10px] text-muted-foreground truncate max-w-[110px]"
                    title={r.past_exhib_participation || ""}>
                  {r.past_exhib_participation ? (
                    <span className="inline-flex items-center gap-1 text-amber-800">
                      <PartyPopper className="w-3 h-3" />
                      {String(r.past_exhib_participation).slice(0, 24)}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono"
                    data-testid={`account-contacts-${r.id}`}>
                  {r.contact_count ? (
                    <span className="inline-flex items-center gap-1 text-primary">
                      <Users className="w-3 h-3" /> {r.contact_count}
                    </span>
                  ) : <span className="text-muted-foreground">0</span>}
                </td>
              </tr>
            ))}
            {!busy && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center px-3 py-8 text-muted-foreground text-xs">
                  No accounts match. Try adjusting filters or import an organizations file.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openAccount && (
        <Dialog open onOpenChange={(v) => { if (!v) setOpenAccount(null); }}>
          <DialogContent className="max-w-2xl" data-testid="account-drawer">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-4 h-4" /> {openAccount.name}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {openAccount.website && (
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Website</div>
                  <a href={openAccount.website.startsWith("http") ? openAccount.website : `https://${openAccount.website}`}
                     target="_blank" rel="noopener noreferrer"
                     className="text-primary hover:underline break-all">{openAccount.website}</a>
                </div>
              )}
              {openAccount.industry && <div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">Industry</div>{openAccount.industry}{openAccount.sub_industry ? ` · ${openAccount.sub_industry}` : ""}</div>}
              {openAccount.country && <div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">Country</div>{openAccount.country}</div>}
              {openAccount.city && <div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">City</div>{openAccount.city}{openAccount.state ? `, ${openAccount.state}` : ""}</div>}
              {openAccount.primary_email && <div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">Primary email</div>{openAccount.primary_email}</div>}
              {openAccount.primary_phone && <div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">Primary phone</div>{openAccount.primary_phone}</div>}
              {openAccount.is_exporter && (
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Exporter</div>
                  <span className="inline-flex items-center gap-1 text-sky-700"><Ship className="w-3 h-3" /> Yes{openAccount.exporting_countries ? ` · ${openAccount.exporting_countries}` : ""}</span>
                </div>
              )}
              {openAccount.past_exhib_participation && (
                <div className="col-span-2">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Past exhibitions</div>
                  <span className="text-[11px]">{openAccount.past_exhib_participation}</span>
                </div>
              )}
            </div>

            <div className="mt-4 border-t border-border pt-3">
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                <Users className="w-3 h-3" /> Contacts at this company
                <span className="text-muted-foreground normal-case tracking-normal">· {contacts.length}</span>
              </div>
              {contactsBusy && <div className="text-[11px] text-muted-foreground italic">Loading contacts…</div>}
              {!contactsBusy && contacts.length === 0 && (
                <div className="text-[11px] text-muted-foreground italic"
                     data-testid="account-contacts-empty">
                  No contacts linked to this company yet. Import contacts with matching <code>external_company_id</code> = <code>{openAccount.external_id || "—"}</code>.
                </div>
              )}
              {contacts.length > 0 && (
                <div className="max-h-[280px] overflow-auto border border-border">
                  <table className="w-full text-[11px]" data-testid="account-contacts-table">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Name</th>
                        <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Title</th>
                        <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Email</th>
                        <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((c) => (
                        <tr key={c.id}
                            className="border-t border-border hover:bg-muted/30 cursor-pointer"
                            onClick={() => nav(`/app/leads?lead=${c.id}`)}
                            data-testid={`account-contact-${c.id}`}>
                          <td className="px-2 py-1 font-medium truncate max-w-[160px]">{c.name}</td>
                          <td className="px-2 py-1 text-muted-foreground truncate max-w-[160px]">{c.title || "—"}</td>
                          <td className="px-2 py-1 text-muted-foreground truncate max-w-[180px]">{c.email || "—"}</td>
                          <td className="px-2 py-1 text-muted-foreground text-[10px]">{c.mobile || c.phone || c.office_phone || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-3 flex justify-end">
              <Button variant="outline" className="rounded-none" onClick={() => setOpenAccount(null)}
                      data-testid="account-drawer-close">
                <X className="w-3.5 h-3.5 mr-1" /> Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
